'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPublicClient, encodeFunctionData, http, type Hex } from 'viem';
import { base, baseSepolia } from 'viem/chains';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Minus,
  Plus,
  Settings2,
  Timer,
  Wallet,
  AlertTriangle,
  Loader2,
  LogOut,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import { cn, parseGridId } from '@/lib/utils';
import { BoardCover, boardInitials } from '../ui/BoardCover';
import { formatCompactUsd } from '../data/mock-prices';
import {
  useLobbySeatHoldings,
  getOfflineDemoTileTotals,
  type LobbyBoardHolding,
  type LobbySeatRow,
} from '@/hooks/use-lobby-seat-holdings';
import type { SeatPosition } from '@/components/seats/coin-seats-card';
import { useWallet } from '@/hooks/use-wallet';
import { useTransactionQueue } from '@/hooks/use-transactions';
import { useTakeoverPrice } from '@/hooks/use-takeover-price';
import { ERC20_ABI, getContractAddresses } from '@/lib/contracts';
import {
  compactNum,
  getTaxTokenConfig,
  refreshAfterTransaction,
  type TaxTokenConfig,
} from '@/lib/drawer-utils';
import { handleTxError } from '@/components/drawers/sim-error';
import {
  buildManageSteps,
  buildAbandonSteps,
  type DepositMode,
  type ManageSelectionRow,
} from './tile-management-steps';
import { ProtectionFooter } from '@/features/protection/ProtectionFooter';

const fmtUsd = (n: number) => formatCompactUsd(n);

/** @deprecated Use `LobbySeatRow` from `@/hooks/use-lobby-seat-holdings`. */
export type Seat = LobbySeatRow;
/** @deprecated Use `LobbyBoardHolding` from `@/hooks/use-lobby-seat-holdings`. */
export type UserBoardHolding = LobbyBoardHolding;

type BoardChip = {
  id: string;
  name: string;
  hue: string;
  imageUrl?: string | null;
};

type SeatSortKey = 'runway' | 'price' | 'deposit';

/**
 * A single seat in the flat cross-board "Your Tiles" list. Joins the
 * display-shape `LobbySeatRow` (USD floats, for the table) to its
 * board's contract targets + the raw `SeatPosition` bigints (for
 * building calldata). Board context travels on every row because the
 * list mixes seats from every board the holder owns.
 */
interface FlatSeatRow {
  id: string;
  boardId: string;
  boardName: string;
  boardSymbol: string;
  boardImageUrl?: string | null;
  hue: string;
  managerAddress: Hex;
  gridId: bigint;
  memecoinAddress?: string;
  ttc: TaxTokenConfig;
  seatIndex: number;
  seatCode: string;
  price: number;
  deposit: number;
  runwayDays: number;
  weeklyTaxUsd: number;
  lockRisk: LobbySeatRow['lockRisk'];
  isForfeited?: boolean;
  rawPrice: bigint;
  rawBalance: bigint;
}

interface TileManagementModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  tileValue: string;
  coinSymbol: string;
}

export function TileManagementModal({ open, onOpenChange }: TileManagementModalProps) {
  const { holdings, groups, isLoading } = useLobbySeatHoldings();

  const addresses = useMemo(() => getContractAddresses(), []);

  // Flatten every V2 board's seats into one cross-board list. Each row
  // carries its board's manager/grid/tax-token so the bulk actions can
  // fan out per board without a drill-down.
  const flatRows = useMemo<FlatSeatRow[]>(() => {
    const groupByPool = new Map(groups.map((g) => [g.poolId, g]));
    const out: FlatSeatRow[] = [];
    for (const h of holdings) {
      if (!h.isV2) continue;
      const g = groupByPool.get(h.boardId);
      if (!g || !g.managerAddress) continue;
      const ttc = getTaxTokenConfig(g.taxToken, addresses.USDC, g.taxTokenDecimals);
      const gridId = BigInt(parseGridId(g.poolId).numericId || '0');
      const rawByIndex = new Map<number, SeatPosition>();
      for (const p of g.seats) rawByIndex.set(p.seatIndex, p);
      for (const s of h.seats) {
        const raw = rawByIndex.get(s.seatIndex);
        out.push({
          id: s.id,
          boardId: h.boardId,
          boardName: h.boardName,
          boardSymbol: g.poolSymbol,
          boardImageUrl: h.boardImageUrl,
          hue: lobbyBoardHue(h.boardId),
          managerAddress: (g.managerAddress || '') as Hex,
          gridId,
          memecoinAddress: g.memecoinAddress,
          ttc,
          seatIndex: s.seatIndex,
          seatCode: s.seatCode,
          price: s.price,
          deposit: s.deposit,
          runwayDays: s.runwayDays,
          weeklyTaxUsd: s.weeklyTaxUsd,
          lockRisk: s.lockRisk,
          isForfeited: s.isForfeited,
          rawPrice: raw?.selfAssessedPrice ?? 0n,
          rawBalance: raw?.balance ?? 0n,
        });
      }
    }
    return out;
  }, [holdings, groups, addresses.USDC]);

  const boardCount = useMemo(
    () => new Set(flatRows.map((r) => r.boardId)).size,
    [flatRows],
  );
  const totalTileValue = useMemo(
    () => flatRows.reduce((sum, r) => sum + r.price, 0),
    [flatRows],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1040px] w-[96vw] p-0 bg-[#0b0a08] border-amber-500/30 text-zinc-100">
        <DialogHeader className="px-5 py-3 border-b border-amber-500/20">
          <DialogTitle className="text-amber-100 flex items-center gap-2 pr-8 min-w-0">
            <Wallet className="h-4 w-4" />
            Your Tiles
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-[11px] font-mono tracking-[0.2em] uppercase">
            Tile Value · {fmtUsd(totalTileValue)} across {boardCount} board
            {boardCount === 1 ? '' : 's'}
          </DialogDescription>
        </DialogHeader>

        {/* Two-column on desktop: the tile list owns the full-height
            left column; Deposit Protection sits in a calm right rail
            (it stacks below the list on narrow screens). */}
        <div className="h-[600px] max-h-[80vh] overflow-hidden flex flex-col lg:flex-row">
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-zinc-500 text-[11px] font-mono uppercase tracking-[0.2em]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading tiles…
            </div>
          ) : (
            <>
              <div className="flex-1 min-h-0 min-w-0 flex flex-col">
                {flatRows.length === 0 ? <EmptyTiles /> : <AllSeatsView rows={flatRows} />}
              </div>
              <ProtectionFooter groups={groups} />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EmptyTiles() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-2 py-12 text-center text-zinc-500 px-6">
      <Wallet className="h-6 w-6 opacity-40" />
      <span className="text-[11px] font-mono tracking-[0.18em] uppercase">
        No active tiles in portfolio
      </span>
      <span className="text-[10px] text-zinc-600 normal-case tracking-normal max-w-[280px]">
        Tiles you currently hold — including any pending forfeit — appear here. Top up to revive,
        or abandon to release.
      </span>
    </div>
  );
}

function AllSeatsView({ rows }: { rows: FlatSeatRow[] }) {
  const wallet = useWallet();
  const { executeActions } = useTransactionQueue();
  const queryClient = useQueryClient();
  const { priceUsd: takeoverPriceUsd } = useTakeoverPrice();
  const addresses = useMemo(() => getContractAddresses(), []);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // `null` = all boards shown. Otherwise the explicit set of visible board ids.
  const [boardFilter, setBoardFilter] = useState<Set<string> | null>(null);
  const [seatSort, setSeatSort] = useState<SeatSortKey>('runway');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [priceValue, setPriceValue] = useState('');
  const [depositMode, setDepositMode] = useState<DepositMode>('top-up');
  const [depositValue, setDepositValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  // Anchor for shift-click range selection (kept by row id so it survives re-sorts).
  const [anchorId, setAnchorId] = useState<string | null>(null);

  const boards = useMemo<BoardChip[]>(() => {
    const m = new Map<string, BoardChip>();
    for (const r of rows) {
      if (!m.has(r.boardId)) {
        m.set(r.boardId, {
          id: r.boardId,
          name: r.boardName,
          hue: r.hue,
          imageUrl: r.boardImageUrl ?? null,
        });
      }
    }
    return [...m.values()];
  }, [rows]);

  const isBoardShown = useCallback(
    (id: string) => boardFilter === null || boardFilter.has(id),
    [boardFilter],
  );

  const toggleBoard = (id: string) => {
    setBoardFilter((prev) => {
      const base = prev ?? new Set(boards.map((b) => b.id));
      const next = new Set(base);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      // Collapse back to "all shown" when every board is selected.
      return next.size === boards.length ? null : next;
    });
  };

  const visibleRows = useMemo(
    () => rows.filter((r) => isBoardShown(r.boardId)),
    [rows, isBoardShown],
  );

  const sortedRows = useMemo(() => {
    const arr = [...visibleRows];
    // Forfeited tiles ("needs action now") float to the very top in
    // every sort mode; the chosen key orders the rest.
    const forf = (r: FlatSeatRow) => (r.isForfeited ? 0 : 1);
    arr.sort((a, b) => {
      if (forf(a) !== forf(b)) return forf(a) - forf(b);
      let cmp: number;
      if (seatSort === 'price') cmp = a.price - b.price;
      else if (seatSort === 'deposit') cmp = a.deposit - b.deposit;
      else {
        // Treat non-measurable runway (no tax → Infinity) as "park at
        // the far end" so it never crowds the actionable rows.
        const ad = Number.isFinite(a.runwayDays) ? a.runwayDays : Number.POSITIVE_INFINITY;
        const bd = Number.isFinite(b.runwayDays) ? b.runwayDays : Number.POSITIVE_INFINITY;
        cmp = ad - bd;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [visibleRows, seatSort, sortDir]);

  // Click a column header to sort by it; click again to flip direction.
  // Runway defaults to ascending (most-urgent first); the value columns
  // default to descending (largest first).
  const onSort = (col: SeatSortKey) => {
    if (seatSort === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSeatSort(col);
      setSortDir(col === 'runway' ? 'asc' : 'desc');
    }
  };

  // Selection only ever acts on rows currently visible — filtering a
  // board out drops its rows from the effective selection so a bulk
  // action never touches a hidden seat.
  const selectedRows = useMemo(
    () => sortedRows.filter((r) => selected.has(r.id)),
    [sortedRows, selected],
  );

  const allChecked = selectedRows.length > 0 && selectedRows.length === sortedRows.length;
  const someChecked = selectedRows.length > 0 && !allChecked;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(sortedRows.map((r) => r.id)));
  };

  // Row click toggles selection; shift-click selects the contiguous range
  // from the last-clicked anchor over the current sorted/visible order.
  const onRowClick = (e: React.MouseEvent, idx: number, id: string) => {
    if (e.shiftKey && anchorId) {
      const aIdx = sortedRows.findIndex((r) => r.id === anchorId);
      if (aIdx !== -1) {
        const lo = Math.min(aIdx, idx);
        const hi = Math.max(aIdx, idx);
        setSelected((prev) => {
          const next = new Set(prev);
          for (let k = lo; k <= hi; k++) next.add(sortedRows[k].id);
          return next;
        });
        return;
      }
    }
    toggle(id);
    setAnchorId(id);
  };

  // Bulk inputs stay USD-canonical. The USD|TOKEN toggle only makes
  // sense when every selected tile shares one tax token; a mixed
  // selection forces USD (each board converts per its own token at
  // build time).
  const usdFallbackTtc = useMemo(
    () => getTaxTokenConfig(undefined, addresses.USDC, 6),
    [addresses.USDC],
  );
  const selectionTtc = useMemo(() => {
    if (selectedRows.length === 0) return usdFallbackTtc;
    const tokens = new Set(selectedRows.map((r) => r.ttc.tokenAddress.toLowerCase()));
    return tokens.size === 1 ? selectedRows[0].ttc : usdFallbackTtc;
  }, [selectedRows, usdFallbackTtc]);

  const priceTrimmed = priceValue.trim();
  const priceParsed = priceTrimmed === '' ? NaN : parseFloat(priceTrimmed);
  const hasPriceInput = Number.isFinite(priceParsed) && priceParsed >= 0;
  const depositTrimmed = depositValue.trim();
  const depositParsed = depositTrimmed === '' ? 0 : parseFloat(depositTrimmed);
  const hasDepositInput = Number.isFinite(depositParsed) && depositParsed > 0;

  const canAct = !!wallet.address;
  const canApply =
    canAct && selectedRows.length > 0 && (hasPriceInput || hasDepositInput) && !submitting;
  const canAbandon = canAct && selectedRows.length > 0 && !submitting;

  const nextPrice = (s: FlatSeatRow) => {
    if (priceTrimmed === '') return s.price;
    const v = parseFloat(priceTrimmed);
    if (!Number.isFinite(v) || v < 0) return s.price;
    return +v.toFixed(2);
  };

  const nextDeposit = useCallback(
    (s: FlatSeatRow) => {
      const v = depositTrimmed === '' ? 0 : parseFloat(depositTrimmed);
      const delta = Number.isFinite(v) ? v : 0;
      if (depositMode === 'top-up') return +(s.deposit + delta).toFixed(2);
      return +Math.max(0, s.deposit - delta).toFixed(2);
    },
    [depositTrimmed, depositMode],
  );

  const previewRunway = (s: FlatSeatRow, p: number, d: number) => {
    const weekly = s.weeklyTaxUsd > 0 ? s.weeklyTaxUsd : p * 0.05;
    if (weekly <= 0) return Infinity;
    return Math.round((d / weekly) * 7);
  };

  const toSelectionRows = (src: FlatSeatRow[]): ManageSelectionRow[] =>
    src.map((r) => ({
      id: r.id,
      managerAddress: r.managerAddress,
      gridId: r.gridId,
      seatIndex: r.seatIndex,
      ttc: r.ttc,
      rawPrice: r.rawPrice,
      rawBalance: r.rawBalance,
      boardSymbol: r.boardSymbol,
      seatLabel: r.seatCode,
    }));

  // Refresh portfolio caches per affected (manager, board) group so the
  // optimistic RPC re-read lands on the right query keys.
  const refreshSelection = useCallback(
    (src: FlatSeatRow[]) => {
      const byBoard = new Map<
        string,
        { mgr: Hex; poolId: string; memecoin?: string; indices: number[] }
      >();
      for (const r of src) {
        const key = `${r.managerAddress}-${r.boardId}`;
        let e = byBoard.get(key);
        if (!e) {
          e = { mgr: r.managerAddress, poolId: r.boardId, memecoin: r.memecoinAddress, indices: [] };
          byBoard.set(key, e);
        }
        e.indices.push(r.seatIndex);
      }
      for (const e of byBoard.values()) {
        refreshAfterTransaction(queryClient, e.mgr, e.poolId, e.memecoin, e.indices);
      }
    },
    [queryClient],
  );

  const handleApply = useCallback(async () => {
    if (!wallet.address || selectedRows.length === 0) return;
    if (!hasPriceInput && !hasDepositInput) return;

    setSubmitting(true);
    try {
      const built = buildManageSteps(toSelectionRows(selectedRows), {
        priceUsd: hasPriceInput ? priceParsed : null,
        depositUsd: hasDepositInput ? depositParsed : null,
        depositMode,
        takeoverPriceUsd,
      });

      const steps: {
        id: string;
        label: string;
        description?: string;
        call: { to: Hex; data: Hex };
      }[] = [];

      // Approvals first — one per (tax token, manager) that needs more
      // allowance for the top-ups on it.
      if (built.approvals.length > 0) {
        const chain = process.env.NEXT_PUBLIC_CHAIN_ID === '8453' ? base : baseSepolia;
        const rpcUrl = process.env.NEXT_PUBLIC_RPC_URL || chain.rpcUrls.default.http[0];
        const publicClient = createPublicClient({ chain, transport: http(rpcUrl) });
        const symbolFor = (token: Hex) =>
          selectedRows.find(
            (r) => r.ttc.tokenAddress.toLowerCase() === token.toLowerCase(),
          )?.ttc.symbol ?? 'token';
        for (const need of built.approvals) {
          const allowance = (await publicClient.readContract({
            address: need.token,
            abi: ERC20_ABI,
            functionName: 'allowance',
            args: [wallet.address as Hex, need.spender],
          })) as bigint;
          if (allowance < need.total) {
            const sym = symbolFor(need.token);
            steps.push({
              id: `approve-${need.token}-${need.spender}`,
              label: `Approve ${sym}`,
              description: `Approve ${sym} spending`,
              call: {
                to: need.token,
                data: encodeFunctionData({
                  abi: ERC20_ABI,
                  functionName: 'approve',
                  args: [need.spender, need.total * 10n],
                }),
              },
            });
          }
        }
      }

      steps.push(...built.steps);
      if (steps.length === 0) return;

      const parts: string[] = [];
      if (hasPriceInput) parts.push('Reprice');
      if (hasDepositInput) parts.push(depositMode === 'top-up' ? 'Deposit' : 'Withdraw');
      const tileWord = `${selectedRows.length} tile${selectedRows.length === 1 ? '' : 's'}`;
      const boardWord = built.boardCount === 1 ? '' : ` across ${built.boardCount} boards`;
      const title = `${parts.join(' & ')} ${tileWord}${boardWord}`;

      const affected = [...selectedRows];
      await executeActions(title, steps, {
        onSuccess: () => {
          refreshSelection(affected);
          setSelected(new Set());
          setPriceValue('');
          setDepositValue('');
        },
        onError: (err: unknown) => handleTxError(err, 'TileManagementModal.apply'),
      });
    } catch (err) {
      handleTxError(err, 'TileManagementModal.apply');
    } finally {
      setSubmitting(false);
    }
  }, [
    wallet.address,
    selectedRows,
    hasPriceInput,
    hasDepositInput,
    priceParsed,
    depositParsed,
    depositMode,
    takeoverPriceUsd,
    executeActions,
    refreshSelection,
  ]);

  const handleAbandon = useCallback(async () => {
    if (!wallet.address || selectedRows.length === 0) return;
    setConfirmAbandon(false);
    setSubmitting(true);
    try {
      const steps = buildAbandonSteps(toSelectionRows(selectedRows));
      if (steps.length === 0) return;
      const tileWord = `${selectedRows.length} tile${selectedRows.length === 1 ? '' : 's'}`;
      const boardWord = steps.length === 1 ? '' : ` across ${steps.length} boards`;
      const affected = [...selectedRows];
      await executeActions(`Abandon ${tileWord}${boardWord}`, steps, {
        onSuccess: () => {
          refreshSelection(affected);
          setSelected(new Set());
        },
        onError: (err: unknown) => handleTxError(err, 'TileManagementModal.abandon'),
      });
    } catch (err) {
      handleTxError(err, 'TileManagementModal.abandon');
    } finally {
      setSubmitting(false);
    }
  }, [wallet.address, selectedRows, executeActions, refreshSelection]);

  return (
    <div className="flex-1 grid grid-rows-[auto_auto_1fr_auto] min-h-0">
      {/* Controls — board filter chips + sort segmented control. */}
      <div className="px-4 py-2 border-b border-amber-500/15 flex items-center gap-2 flex-wrap">
        <span className="text-[9px] font-mono tracking-[0.22em] uppercase text-zinc-500 mr-1">
          Boards
        </span>
        <button
          type="button"
          onClick={() => setBoardFilter((prev) => (prev === null ? new Set<string>() : null))}
          className={cn(
            'h-6 px-2 rounded-[3px] border text-[10px] font-mono tracking-[0.14em] uppercase transition-colors',
            boardFilter === null
              ? 'bg-amber-500/15 border-amber-400/50 text-amber-100'
              : 'bg-black/30 border-white/10 text-zinc-400 hover:text-amber-100',
          )}
        >
          All
        </button>
        {boards.map((b) => {
          const on = isBoardShown(b.id);
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => toggleBoard(b.id)}
              className={cn(
                'h-6 pl-1 pr-2 inline-flex items-center gap-1.5 rounded-[3px] border text-[10px] font-mono tracking-[0.1em] transition-colors',
                on
                  ? 'bg-amber-500/[0.08] border-amber-400/40 text-amber-100'
                  : 'bg-black/30 border-white/10 text-zinc-500 hover:text-zinc-200',
              )}
              title={b.name}
            >
              <BoardCover
                seed={b.id}
                hue={b.hue}
                initials={boardInitials(b.name)}
                imageUrl={b.imageUrl ?? null}
                size={14}
              />
              <span className="max-w-[88px] truncate">{b.name}</span>
            </button>
          );
        })}
      </div>

      <div className="px-5 py-2 border-b border-amber-500/15 flex items-center gap-3 text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-400">
        <span className="text-amber-100">{sortedRows.length} tiles</span>
        {selectedRows.length > 0 && (
          <>
            <span className="text-zinc-600">·</span>
            <span className="text-amber-200">{selectedRows.length} selected</span>
          </>
        )}
      </div>

      <div className="overflow-auto">
        <table className="w-full text-[11px] [&_td]:align-middle">
          {/*
            Fully-opaque sticky header (was `bg-[#0b0a08]/95 backdrop-blur`).
            A backdrop-blur on a sticky element re-samples + re-blurs the
            rows scrolling beneath it every frame, which was the main cause
            of laggy scrolling in this tile list. At 95% opacity the blur
            added almost nothing visually, so an opaque background is a
            near-identical look without the per-frame GPU cost.
          */}
          <thead className="sticky top-0 bg-[#0b0a08] text-[9px] font-mono tracking-[0.22em] uppercase text-zinc-500">
            <tr className="border-b border-amber-500/10">
              <th className="px-4 py-2 text-left w-10">
                <Checkbox
                  checked={allChecked || (someChecked ? ('indeterminate' as unknown as boolean) : false)}
                  onCheckedChange={toggleAll}
                  className="border-amber-400/40 data-[state=checked]:bg-amber-500 data-[state=checked]:text-black"
                />
              </th>
              <th className="px-1 py-2 text-left">Board · Tile</th>
              <SortableTh label="Price" col="price" seatSort={seatSort} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Deposit" col="deposit" seatSort={seatSort} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Runway" col="runway" seatSort={seatSort} sortDir={sortDir} onSort={onSort} />
              <th className="px-4 py-2 text-right">Risk</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((s, i) => {
              const isSel = selected.has(s.id);
              const diffPrice = isSel ? nextPrice(s) - s.price : 0;
              const diffDep = isSel ? nextDeposit(s) - s.deposit : 0;
              const isForfeit = !!s.isForfeited;
              return (
                <tr
                  key={s.id}
                  onClick={(e) => onRowClick(e, i, s.id)}
                  className={cn(
                    'border-b border-white/[0.04] hover:bg-white/[0.02] h-[58px] cursor-pointer select-none',
                    isSel && (isForfeit ? 'bg-red-500/[0.08]' : 'bg-amber-500/[0.06]'),
                    isForfeit && !isSel && 'bg-red-500/[0.03]',
                  )}
                >
                  <td className="px-4">
                    <Checkbox
                      checked={isSel}
                      tabIndex={-1}
                      aria-hidden
                      className={cn(
                        'pointer-events-none border-amber-400/40 data-[state=checked]:bg-amber-500 data-[state=checked]:text-black',
                        isForfeit && 'border-red-400/50 data-[state=checked]:bg-red-500 data-[state=checked]:text-white',
                      )}
                    />
                  </td>
                  <td className="px-1">
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <BoardCover
                        seed={s.boardId}
                        hue={s.hue}
                        initials={boardInitials(s.boardName)}
                        imageUrl={s.boardImageUrl ?? null}
                        size={18}
                        className="shrink-0"
                      />
                      <span className="flex flex-col min-w-0 leading-tight">
                        <span className="text-[10px] text-zinc-400 truncate max-w-[120px]">
                          {s.boardName}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span
                            className={cn(
                              'font-mono font-semibold',
                              isForfeit ? 'text-red-200' : 'text-amber-100',
                            )}
                          >
                            {s.seatCode}
                          </span>
                          {isForfeit && (
                            <span
                              title="Deposit depleted — anyone can poke this seat into forfeit. Top up to revive, or abandon to release."
                              className="text-[8px] font-mono tracking-[0.14em] uppercase text-red-200 bg-red-500/15 border border-red-400/40 rounded-[2px] px-1"
                            >
                              Forfeit
                            </span>
                          )}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td className="px-1 text-right tabular-nums">
                    <div className={cn(isForfeit ? 'text-red-200/80' : 'text-amber-100')}>{fmtUsd(s.price)}</div>
                    <DiffLine
                      show={isSel && diffPrice !== 0}
                      className={diffPrice > 0 ? 'text-emerald-300' : 'text-orange-300'}
                    >
                      → {fmtUsd(nextPrice(s))}
                    </DiffLine>
                  </td>
                  <td className="px-1 text-right tabular-nums">
                    <div className={cn(isForfeit ? 'text-red-300' : 'text-amber-100')}>
                      {fmtUsd(s.deposit)}
                    </div>
                    <DiffLine
                      show={isSel && diffDep !== 0}
                      className={diffDep > 0 ? 'text-emerald-300' : 'text-orange-300'}
                    >
                      → {fmtUsd(nextDeposit(s))}
                    </DiffLine>
                  </td>
                  <td className="px-1 text-right tabular-nums">
                    <div className={cn(
                      'inline-flex items-center gap-1',
                      isForfeit ? 'text-red-300' : 'text-zinc-200',
                    )}>
                      <Timer className="h-2.5 w-2.5 text-zinc-500" />
                      {isForfeit ? '0d' : s.runwayDays < 100 ? `${s.runwayDays.toFixed(1)}d` : '∞'}
                    </div>
                    <DiffLine
                      show={isSel && (diffPrice !== 0 || diffDep !== 0)}
                      className="text-amber-200"
                    >
                      →{' '}
                      {(() => {
                        const r = previewRunway(s, nextPrice(s), nextDeposit(s));
                        return Number.isFinite(r) ? `${r}d` : '—';
                      })()}
                    </DiffLine>
                  </td>
                  <td className="px-4 text-right">
                    <RiskPill risk={s.lockRisk} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        className={cn(
          'border-t border-amber-500/20 bg-black/60 p-4 space-y-3 transition-opacity duration-150',
          selectedRows.length === 0 && 'opacity-45 pointer-events-none select-none',
        )}
        aria-disabled={selectedRows.length === 0}
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono tracking-[0.22em] uppercase text-amber-200">
            Bulk · {selectedRows.length} selected
          </span>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            disabled={selectedRows.length === 0}
            className="ml-auto text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-400 hover:text-zinc-100 disabled:opacity-50"
          >
            Clear
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <BulkPriceCtrl
            value={priceValue}
            onValue={setPriceValue}
            ttc={selectionTtc}
            takeoverPriceUsd={takeoverPriceUsd}
          />
          <BulkDepositCtrl
            mode={depositMode}
            value={depositValue}
            onMode={setDepositMode}
            onValue={setDepositValue}
            ttc={selectionTtc}
            takeoverPriceUsd={takeoverPriceUsd}
            tileCount={selectedRows.length}
          />
        </div>

        {confirmAbandon ? (
          <div className="flex items-center gap-2 pt-1 rounded-[4px] border border-red-500/40 bg-red-500/[0.08] px-2 py-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-red-300 shrink-0" />
            <span className="text-[10px] font-mono tracking-[0.18em] uppercase text-red-200 flex-1">
              Abandon {selectedRows.length} tile{selectedRows.length === 1 ? '' : 's'}? Balance returned to wallet.
            </span>
            <button
              type="button"
              onClick={() => setConfirmAbandon(false)}
              className="h-7 px-2 rounded-[3px] border border-white/10 text-[10px] font-mono tracking-[0.18em] uppercase text-zinc-200 hover:text-white hover:border-white/30"
            >
              Keep
            </button>
            <button
              type="button"
              onClick={handleAbandon}
              className="h-7 px-2 rounded-[3px] border border-red-400 bg-red-500 text-black text-[10px] font-mono font-semibold tracking-[0.18em] uppercase hover:bg-red-400"
            >
              Confirm abandon
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              disabled={!canApply}
              onClick={handleApply}
              className={cn(
                'flex-1 h-9 rounded-[4px] border text-[11px] font-semibold uppercase tracking-[0.18em] flex items-center justify-center gap-1.5 transition-colors',
                canApply
                  ? 'bg-amber-500 hover:bg-amber-400 border-amber-400 text-black cursor-pointer'
                  : 'bg-amber-500/60 border-amber-400/60 text-black/80 cursor-not-allowed',
              )}
            >
              {submitting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Settings2 className="h-3.5 w-3.5" />
              )}
              {selectedRows.length === 0
                ? 'Select tiles to enable bulk actions'
                : !hasPriceInput && !hasDepositInput
                ? 'Enter price or deposit to apply'
                : `Apply to ${selectedRows.length} tile${selectedRows.length === 1 ? '' : 's'}`}
            </button>
            <button
              type="button"
              disabled={!canAbandon}
              onClick={() => setConfirmAbandon(true)}
              title="Abandon selected tiles and reclaim deposit"
              className={cn(
                'h-9 px-3 rounded-[4px] border text-[10px] font-mono font-semibold uppercase tracking-[0.18em] flex items-center gap-1.5 transition-colors',
                canAbandon
                  ? 'border-red-400/60 bg-red-500/10 text-red-200 hover:bg-red-500/20 hover:border-red-300 cursor-pointer'
                  : 'border-red-400/30 bg-red-500/5 text-red-200/50 cursor-not-allowed',
              )}
            >
              <LogOut className="h-3.5 w-3.5" />
              Abandon
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SortableTh({
  label,
  col,
  seatSort,
  sortDir,
  onSort,
}: {
  label: string;
  col: SeatSortKey;
  seatSort: SeatSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (col: SeatSortKey) => void;
}) {
  const active = seatSort === col;
  return (
    <th
      className="px-1 py-2 text-right"
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-0.5 text-[9px] font-mono tracking-[0.22em] uppercase text-zinc-500 hover:text-amber-100 transition-colors cursor-pointer"
      >
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ChevronUp className="h-2.5 w-2.5 text-amber-300" />
          ) : (
            <ChevronDown className="h-2.5 w-2.5 text-amber-300" />
          )
        ) : (
          <ChevronsUpDown className="h-2.5 w-2.5 text-zinc-600" />
        )}
      </button>
    </th>
  );
}

function DiffLine({
  show,
  className,
  children,
}: {
  show: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'h-[13px] text-[9px] font-mono leading-[13px] transition-opacity',
        show ? 'opacity-100' : 'opacity-0',
        className,
      )}
      aria-hidden={!show}
    >
      {children}
    </div>
  );
}

/**
 * Two-segment "USD | TOKEN" pill used by the bulk price and deposit
 * controls when the selected tiles share a single non-USDC tax token.
 *
 * NOTE: only rendered when an oracle exists for the alt token (today
 * that means TAKEOVER, since `takeoverPriceUsd` is the only price feed
 * piped through). Mixed-token selections force USD via `isDual=false`.
 */
function CurrencyToggle({
  unit,
  onChange,
  tokenSymbol,
}: {
  unit: 'USD' | 'TOKEN';
  onChange: (next: 'USD' | 'TOKEN') => void;
  tokenSymbol: string;
}) {
  const tab =
    'px-1.5 h-[18px] text-[8.5px] font-mono uppercase tracking-[0.16em] flex items-center transition-colors';
  return (
    <div
      className="inline-flex items-stretch rounded-[3px] overflow-hidden border border-white/[0.08]"
      role="group"
      aria-label="Input currency"
    >
      <button
        type="button"
        aria-pressed={unit === 'USD'}
        onClick={() => onChange('USD')}
        className={cn(
          tab,
          unit === 'USD' ? 'bg-amber-500/15 text-amber-200' : 'text-zinc-400 hover:text-zinc-100',
        )}
      >
        USD
      </button>
      <button
        type="button"
        aria-pressed={unit === 'TOKEN'}
        onClick={() => onChange('TOKEN')}
        className={cn(
          tab,
          unit === 'TOKEN' ? 'bg-amber-500/15 text-amber-200' : 'text-zinc-400 hover:text-zinc-100',
        )}
      >
        {tokenSymbol}
      </button>
    </div>
  );
}

type CurrencyUnit = 'USD' | 'TOKEN';

function formatTokenForInput(num: number): string {
  if (!Number.isFinite(num) || num <= 0) return '';
  if (num >= 1) return num.toFixed(2);
  return num.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
}

function formatUsdForInput(num: number): string {
  if (!Number.isFinite(num) || num <= 0) return '';
  if (num >= 0.01) return num.toFixed(2);
  return num.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * Hook that turns a parent-canonical USD `value` string into a
 * unit-aware displayed text + an alt-currency estimate. The parent
 * always reads/writes a USD string; all unit conversion lives here so
 * the bulk apply path keeps operating on USD floats.
 */
function useUnitAwareInput({
  value,
  onValue,
  isDual,
  takeoverPriceUsd,
  multiplier = 1,
}: {
  value: string;
  onValue: (next: string) => void;
  isDual: boolean;
  takeoverPriceUsd: number;
  tokenSymbol: string;
  multiplier?: number;
}) {
  const [unit, setUnit] = useState<CurrencyUnit>('USD');
  const [localText, setLocalText] = useState<string>('');
  const focusedRef = useRef(false);

  // Force back to USD whenever the selection stops being single-token
  // (isDual flips false) so a stale TOKEN unit can't misread a mixed
  // selection.
  useEffect(() => {
    if (!isDual && unit !== 'USD') setUnit('USD');
  }, [isDual, unit]);

  useEffect(() => {
    if (focusedRef.current) return;
    if (!isDual || unit === 'USD') {
      setLocalText(value);
      return;
    }
    const usd = parseFloat(value || '0');
    if (!Number.isFinite(usd) || usd <= 0) {
      setLocalText('');
      return;
    }
    if (takeoverPriceUsd <= 0) {
      setLocalText(value);
      return;
    }
    setLocalText(formatTokenForInput(usd / takeoverPriceUsd));
  }, [value, unit, isDual, takeoverPriceUsd]);

  const onChange = useCallback(
    (raw: string) => {
      setLocalText(raw);
      const trimmed = raw.trim();
      if (trimmed === '') {
        onValue('');
        return;
      }
      const num = parseFloat(trimmed);
      if (!Number.isFinite(num) || num < 0) {
        onValue(raw);
        return;
      }
      if (!isDual || unit === 'USD' || takeoverPriceUsd <= 0) {
        onValue(raw);
        return;
      }
      onValue((num * takeoverPriceUsd).toFixed(6));
    },
    [isDual, unit, takeoverPriceUsd, onValue],
  );

  const onFocus = useCallback(() => {
    focusedRef.current = true;
  }, []);

  const onBlur = useCallback(() => {
    focusedRef.current = false;
    if (!isDual || unit === 'USD' || takeoverPriceUsd <= 0) {
      const usd = parseFloat(value || '0');
      setLocalText(Number.isFinite(usd) && usd > 0 ? formatUsdForInput(usd) : value);
      return;
    }
    const usd = parseFloat(value || '0');
    if (!Number.isFinite(usd) || usd <= 0) {
      setLocalText('');
      return;
    }
    setLocalText(formatTokenForInput(usd / takeoverPriceUsd));
  }, [value, unit, isDual, takeoverPriceUsd]);

  const onUnitChange = useCallback(
    (next: CurrencyUnit) => {
      setUnit(next);
      const usd = parseFloat(value || '0');
      if (!Number.isFinite(usd) || usd <= 0) {
        setLocalText('');
        return;
      }
      if (next === 'USD' || takeoverPriceUsd <= 0) {
        setLocalText(formatUsdForInput(usd));
      } else {
        setLocalText(formatTokenForInput(usd / takeoverPriceUsd));
      }
    },
    [value, takeoverPriceUsd],
  );

  const altEstimate = useMemo(() => {
    if (!isDual || takeoverPriceUsd <= 0) return null;
    const usd = parseFloat(value || '0');
    if (!Number.isFinite(usd) || usd <= 0) return null;
    const safeMultiplier = Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
    const totalUsd = usd * safeMultiplier;
    if (unit === 'USD') {
      const tok = totalUsd / takeoverPriceUsd;
      return `≈ ${compactNum(tok)} TAKEOVER`;
    }
    return totalUsd >= 0.01 ? `≈ $${totalUsd.toFixed(2)}` : `≈ $${totalUsd.toFixed(4)}`;
  }, [value, unit, isDual, takeoverPriceUsd, multiplier]);

  const showUsdPrefix = !isDual || unit === 'USD' || takeoverPriceUsd <= 0;

  return {
    unit,
    onUnitChange,
    text: localText,
    onChange,
    onFocus,
    onBlur,
    altEstimate,
    showUsdPrefix,
  };
}

function BulkPriceCtrl({
  value,
  onValue,
  ttc,
  takeoverPriceUsd,
}: {
  value: string;
  onValue: (v: string) => void;
  ttc: TaxTokenConfig;
  takeoverPriceUsd: number;
}) {
  const isDual = ttc.isTakeover && takeoverPriceUsd > 0;
  const ui = useUnitAwareInput({
    value,
    onValue,
    isDual,
    takeoverPriceUsd,
    tokenSymbol: ttc.symbol,
  });

  return (
    <div className="rounded-[4px] bg-black/40 border border-white/[0.06] p-2.5">
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <div className="text-[9px] font-mono tracking-[0.22em] uppercase text-zinc-500">
          New price
        </div>
        {isDual && (
          <CurrencyToggle unit={ui.unit} onChange={ui.onUnitChange} tokenSymbol={ttc.symbol} />
        )}
      </div>
      <div className="relative">
        {ui.showUsdPrefix && (
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] font-mono text-zinc-500">
            $
          </span>
        )}
        <input
          type="text"
          value={ui.text}
          inputMode="decimal"
          placeholder={ui.showUsdPrefix ? 'Leave blank to keep current' : `Amount in ${ttc.symbol}`}
          onChange={(e) => ui.onChange(e.target.value)}
          onFocus={ui.onFocus}
          onBlur={ui.onBlur}
          className={cn(
            'w-full h-8 pr-2 text-[12px] font-mono tabular-nums rounded-[3px] bg-black/40 border border-white/[0.08] text-amber-100 placeholder:text-zinc-600 placeholder:normal-case focus:outline-none focus:border-amber-400/60',
            ui.showUsdPrefix ? 'pl-5' : 'pl-2',
          )}
        />
      </div>
      {ui.altEstimate && (
        <div className="mt-1 text-[10px] font-mono text-zinc-400 tabular-nums">{ui.altEstimate}</div>
      )}
      <div className="mt-1 text-[9px] font-mono tracking-[0.15em] uppercase text-zinc-500">
        Applies to every selected tile
      </div>
    </div>
  );
}

function BulkDepositCtrl({
  mode,
  value,
  onMode,
  onValue,
  ttc,
  takeoverPriceUsd,
  tileCount,
}: {
  mode: DepositMode;
  value: string;
  onMode: (m: DepositMode) => void;
  onValue: (v: string) => void;
  ttc: TaxTokenConfig;
  takeoverPriceUsd: number;
  tileCount: number;
}) {
  const isDual = ttc.isTakeover && takeoverPriceUsd > 0;
  const showMultiplier = tileCount > 1;
  const ui = useUnitAwareInput({
    value,
    onValue,
    isDual,
    takeoverPriceUsd,
    tokenSymbol: ttc.symbol,
    multiplier: tileCount,
  });

  return (
    <div className="rounded-[4px] bg-black/40 border border-white/[0.06] p-2.5">
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <div className="text-[9px] font-mono tracking-[0.22em] uppercase text-zinc-500">
          Deposit change
        </div>
        {isDual && (
          <CurrencyToggle unit={ui.unit} onChange={ui.onUnitChange} tokenSymbol={ttc.symbol} />
        )}
      </div>
      <div className="flex items-stretch gap-1.5">
        <div className="flex rounded-[3px] overflow-hidden border border-white/[0.08]">
          <button
            type="button"
            onClick={() => onMode('top-up')}
            className={cn(
              'px-2 text-[10px] font-mono uppercase tracking-[0.15em] flex items-center gap-1',
              mode === 'top-up' ? 'bg-emerald-500/15 text-emerald-200' : 'text-zinc-400 hover:text-zinc-100',
            )}
          >
            <Plus className="h-3 w-3" /> Top up
          </button>
          <button
            type="button"
            onClick={() => onMode('withdraw')}
            className={cn(
              'px-2 text-[10px] font-mono uppercase tracking-[0.15em] flex items-center gap-1',
              mode === 'withdraw' ? 'bg-orange-500/15 text-orange-200' : 'text-zinc-400 hover:text-zinc-100',
            )}
          >
            <Minus className="h-3 w-3" /> Withdraw
          </button>
        </div>
        <div className="relative flex-1">
          {ui.showUsdPrefix && (
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] font-mono text-zinc-500">
              $
            </span>
          )}
          <input
            type="text"
            value={ui.text}
            inputMode="decimal"
            placeholder={ui.showUsdPrefix ? '0 = no change' : `Amount in ${ttc.symbol}`}
            onChange={(e) => ui.onChange(e.target.value)}
            onFocus={ui.onFocus}
            onBlur={ui.onBlur}
            className={cn(
              'w-full h-8 text-[12px] font-mono tabular-nums rounded-[3px] bg-black/40 border border-white/[0.08] text-amber-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-400/60',
              ui.showUsdPrefix ? 'pl-5' : 'pl-2',
              showMultiplier ? 'pr-[5.5em]' : 'pr-2',
            )}
          />
          {showMultiplier && (
            <span
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono tracking-[0.12em] text-zinc-500 pointer-events-none select-none"
              aria-hidden
            >
              × {tileCount} tiles
            </span>
          )}
        </div>
      </div>
      {ui.altEstimate && (
        <div className="mt-1 text-[10px] font-mono text-zinc-400 tabular-nums">
          {ui.altEstimate}
          {showMultiplier && <span className="ml-1 text-zinc-500 normal-case">total</span>}
        </div>
      )}
      <div className="mt-1 text-[9px] font-mono tracking-[0.15em] uppercase text-zinc-500">
        {mode === 'top-up'
          ? 'Adds to each tile — extends runway'
          : 'Withdraws from each tile — shortens runway'}
      </div>
    </div>
  );
}

function RiskPill({ risk }: { risk: LobbySeatRow['lockRisk'] }) {
  const map = {
    safe: { label: 'Safe', hue: '#22c55e' },
    warn: { label: 'Watch', hue: '#facc15' },
    danger: { label: 'At risk', hue: '#f97316' },
  }[risk];
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 h-4 rounded-[2px] text-[8.5px] font-mono tracking-[0.18em] uppercase border"
      style={{ color: map.hue, borderColor: map.hue + '55', backgroundColor: map.hue + '14' }}
    >
      <span className="w-1 h-1 rounded-full" style={{ backgroundColor: map.hue }} />
      {map.label}
    </span>
  );
}

/** Offline / demo fallback for nav when wallet hooks are inactive — returns zero totals. */
export function getTotalTileValue(): { total: number; coinSymbol: string } {
  return getOfflineDemoTileTotals();
}

/**
 * @deprecated Live runway alerts use `useLobbyHeaderStats` in `LobbyNav`.
 */
export function getTileAlertStatus(): {
  risk: 'safe' | 'warn' | 'danger';
  count: number;
  minRunwayDays: number;
} {
  return { risk: 'safe', count: 0, minRunwayDays: 0 };
}

/** Deterministic cover hue for a pool/grid id (shared with profile board rows). */
export function lobbyBoardHue(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const hueDeg = h % 360;
  return hslToHex(hueDeg, 70, 55);
}

function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sNorm * Math.min(lNorm, 1 - lNorm);
  const f = (n: number) => {
    const v = lNorm - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(v * 255);
  };
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}
